import { useMemo, type ReactNode } from "react";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { Ago, SectionCard } from "../../components/primitives";
import { TimeSeriesChart, type ChartSeries } from "../../components/time-series-chart";
import { formatCount, formatMb } from "../../format";
import { SERIES, STATUS } from "../../palette";
import type { MetricSeries, ProcessSample, RuntimeSection } from "../../types";
import { LiveChart, type LiveRow, type LiveSeries } from "./live-chart";
import { StatTile, SubHeading } from "./stat-tile";
import { HOUR_MS, LIMITS, toEpochMs } from "./tone";

const formatPct = (value: number | null | undefined) =>
  value === null || value === undefined ? "–" : `${formatCount(value)} %`;
const formatPctTick = (value: number) => `${formatCount(value)} %`;
const formatMbValue = (value: number) => formatMb(value);

/** CPU, memory, tasks and work in progress: the current sample, the last hour of samples, window peaks. */
export function ResourcesCard({ runtime, metrics }: { runtime: RuntimeSection; metrics: MetricSeries | undefined }) {
  const { t } = useLocalization();
  const sample = runtime.sample as Partial<ProcessSample>;
  const sampledAt = toEpochMs(sample.ts);
  const writes = sample.background_writes ?? null;

  const rows = useMemo<LiveRow[]>(
    () =>
      runtime.samples
        .filter((s) => typeof s.ts === "number")
        .map((s) => ({
          ts: toEpochMs(s.ts) as number,
          cpu: s.cpu_pct,
          rss: s.rss_mb,
          tasks: s.tasks,
          threads: s.threads,
          fds: s.fds,
          writes: s.background_writes ?? null,
          inference: s.inference_in_flight ?? null,
          http: s.http_in_flight ?? null,
          monitored: s.monitored_tasks ?? null,
        })),
    [runtime.samples],
  );

  const live = useMemo(
    () => ({
      cpu: [{ key: "cpu", label: t("monitoring.backend.res.cpu"), color: SERIES[0] }] satisfies LiveSeries[],
      rss: [{ key: "rss", label: t("monitoring.backend.res.rss"), color: SERIES[0] }] satisfies LiveSeries[],
      counts: [
        { key: "tasks", label: t("monitoring.backend.res.tasks"), color: SERIES[0] },
        { key: "threads", label: t("monitoring.backend.res.threads"), color: SERIES[1] },
        { key: "fds", label: t("monitoring.backend.res.fds"), color: SERIES[2] },
      ] satisfies LiveSeries[],
      work: [
        { key: "writes", label: t("monitoring.backend.res.writes"), color: SERIES[0] },
        { key: "inference", label: t("monitoring.backend.res.inference"), color: SERIES[1] },
        { key: "http", label: t("monitoring.backend.res.http"), color: SERIES[2] },
        { key: "monitored", label: t("monitoring.backend.res.monitored"), color: SERIES[3] },
      ] satisfies LiveSeries[],
    }),
    [t],
  );

  const peaks = useMemo(() => {
    if (!metrics) return null;
    const max = (name: string) => metrics.maxes[name] ?? [];
    return {
      cpu: [{ key: "cpu", label: t("monitoring.backend.res.cpu"), color: SERIES[0], values: max("proc.cpu_pct"), type: "line" }] satisfies ChartSeries[],
      rss: [{ key: "rss", label: t("monitoring.backend.res.rss"), color: SERIES[0], values: max("proc.rss_mb"), type: "line" }] satisfies ChartSeries[],
      tasks: [{ key: "tasks", label: t("monitoring.backend.res.tasks"), color: SERIES[0], values: max("proc.tasks"), type: "line" }] satisfies ChartSeries[],
      work: [
        { key: "writes", label: t("monitoring.backend.res.writes"), color: SERIES[0], values: max("background.writes"), type: "line" },
        { key: "inference", label: t("monitoring.backend.res.inference"), color: SERIES[1], values: max("pod.in_flight"), type: "line" },
        { key: "http", label: t("monitoring.backend.res.http"), color: SERIES[2], values: max("http.in_flight"), type: "line" },
      ] satisfies ChartSeries[],
    };
  }, [metrics, t]);

  const empty = t("monitoring.backend.res.noSamples");
  const backlogLine = [{ y: LIMITS.backgroundWritesBacklog, label: t("monitoring.backend.res.backlog"), color: STATUS.warning }];

  return (
    <SectionCard
      title={t("monitoring.backend.res.title")}
      description={t("monitoring.backend.res.description")}
      actions={
        sampledAt ? (
          <span className="text-xs text-muted-foreground">
            {t("monitoring.backend.res.sampled")} <Ago ts={sampledAt} />
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-9">
          <StatTile label={t("monitoring.backend.res.cpu")} value={formatPct(sample.cpu_pct)} sub={t("monitoring.backend.res.cpuSub")} />
          <StatTile label={t("monitoring.backend.res.rss")} value={formatMb(sample.rss_mb ?? null)} />
          <StatTile label={t("monitoring.backend.res.tasks")} value={formatCount(sample.tasks)} />
          <StatTile label={t("monitoring.backend.res.threads")} value={formatCount(sample.threads)} />
          <StatTile label={t("monitoring.backend.res.fds")} value={formatCount(sample.fds)} />
          <StatTile
            label={t("monitoring.backend.res.writes")}
            value={formatCount(writes)}
            tone={writes !== null && writes >= LIMITS.backgroundWritesBacklog ? "warn" : "neutral"}
            sub={t("monitoring.backend.res.writesSub")}
          />
          <StatTile label={t("monitoring.backend.res.inference")} value={formatCount(sample.inference_in_flight)} />
          <StatTile label={t("monitoring.backend.res.http")} value={formatCount(sample.http_in_flight)} />
          <StatTile
            label={t("monitoring.backend.res.monitored")}
            value={formatCount(sample.monitored_tasks ?? runtime.monitored_tasks)}
            sub={t("monitoring.backend.res.monitoredSub")}
          />
        </div>

        <div className="flex flex-col gap-3">
          <SubHeading>{t("monitoring.backend.res.live")}</SubHeading>
          <div className="grid grid-cols-1 gap-x-6 gap-y-5 xl:grid-cols-2">
            <ChartBlock title={t("monitoring.backend.res.chart.cpu")}>
              <LiveChart rows={rows} series={live.cpu} spanMs={HOUR_MS} format={formatPctTick} tickFormat={formatPctTick} emptyText={empty} />
            </ChartBlock>
            <ChartBlock title={t("monitoring.backend.res.chart.memory")}>
              <LiveChart rows={rows} series={live.rss} spanMs={HOUR_MS} format={formatMbValue} tickFormat={formatMbValue} emptyText={empty} />
            </ChartBlock>
            <ChartBlock title={t("monitoring.backend.res.chart.counts")}>
              <LiveChart rows={rows} series={live.counts} spanMs={HOUR_MS} integer format={formatCount} emptyText={empty} />
            </ChartBlock>
            <ChartBlock title={t("monitoring.backend.res.chart.work")}>
              <LiveChart
                rows={rows}
                series={live.work}
                spanMs={HOUR_MS}
                integer
                format={formatCount}
                referenceLines={backlogLine}
                emptyText={empty}
              />
            </ChartBlock>
          </div>
        </div>

        {metrics && peaks && (
          <div className="flex flex-col gap-3">
            <SubHeading>
              {t("monitoring.backend.res.window", { window: t(`monitoring.window.long.${metrics.window}` as TranslationKey) })}
            </SubHeading>
            <div className="grid grid-cols-1 gap-x-6 gap-y-5 xl:grid-cols-2">
              <ChartBlock title={t("monitoring.backend.res.chart.cpuMax")}>
                <TimeSeriesChart
                  t={metrics.t}
                  stepMs={metrics.step_ms}
                  window={metrics.window}
                  series={peaks.cpu}
                  height={160}
                  format={formatPctTick}
                  tickFormat={formatPctTick}
                  emptyText={t("monitoring.noData")}
                />
              </ChartBlock>
              <ChartBlock title={t("monitoring.backend.res.chart.memoryMax")}>
                <TimeSeriesChart
                  t={metrics.t}
                  stepMs={metrics.step_ms}
                  window={metrics.window}
                  series={peaks.rss}
                  height={160}
                  format={formatMbValue}
                  tickFormat={formatMbValue}
                  emptyText={t("monitoring.noData")}
                />
              </ChartBlock>
              <ChartBlock title={t("monitoring.backend.res.chart.tasksMax")}>
                <TimeSeriesChart
                  t={metrics.t}
                  stepMs={metrics.step_ms}
                  window={metrics.window}
                  series={peaks.tasks}
                  height={160}
                  integer
                  format={formatCount}
                  emptyText={t("monitoring.noData")}
                />
              </ChartBlock>
              <ChartBlock title={t("monitoring.backend.res.chart.workMax")}>
                <TimeSeriesChart
                  t={metrics.t}
                  stepMs={metrics.step_ms}
                  window={metrics.window}
                  series={peaks.work}
                  height={160}
                  integer
                  format={formatCount}
                  referenceLines={backlogLine}
                  emptyText={t("monitoring.backend.res.noWork")}
                />
              </ChartBlock>
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

function ChartBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="text-xs font-medium text-foreground">{title}</div>
      {children}
    </div>
  );
}
